import { CameraOutlined } from "@ant-design/icons";
import { Alert, Button, Modal } from "antd";
import { useEffect, useRef, useState } from "react";

/** The browser's own barcode reader (Chrome on Android, Samsung Internet); absent on iOS Safari and Firefox. */
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike;

const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "code_93", "codabar", "itf", "qr_code", "data_matrix", "pdf417", "aztec"];

export function cameraScanSupported(): boolean {
  return typeof window !== "undefined" && "BarcodeDetector" in window && !!navigator.mediaDevices?.getUserMedia;
}

/** «Сканировать камерой» for the work screen in a phone browser: the first recognised code goes to `onScan`. */
export function CameraScanButton({ onScan, size = "large" }: { onScan: (value: string) => void; size?: "large" | "middle" }) {
  const [open, setOpen] = useState(false);
  if (!cameraScanSupported()) return null;
  return (
    <>
      <Button size={size} icon={<CameraOutlined />} onClick={() => setOpen(true)}>
        Сканировать камерой
      </Button>
      {open && (
        <CameraScanModal
          onClose={() => setOpen(false)}
          onScan={(value) => {
            setOpen(false);
            onScan(value);
          }}
        />
      )}
    </>
  );
}

function CameraScanModal({ onScan, onClose }: { onScan: (value: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let done = false;
    const Detector = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor }).BarcodeDetector;
    const detector = new Detector({ formats: FORMATS });
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      .then((s) => {
        stream = s;
        if (!video.current) return;
        video.current.srcObject = s;
        return video.current.play();
      })
      .then(() => {
        timer = window.setInterval(async () => {
          if (done || !video.current || video.current.readyState < 2) return;
          try {
            const codes = await detector.detect(video.current);
            const value = codes.find((c) => c.rawValue?.trim())?.rawValue.trim();
            if (value) {
              done = true;
              onScan(value);
            }
          } catch {
            /* a frame that could not be decoded: try the next one */
          }
        }, 200);
      })
      .catch((e: unknown) => {
        const name = e instanceof Error ? e.name : "";
        setError(name === "NotAllowedError" ? "Доступ к камере запрещён. Разрешите камеру для этого сайта в настройках браузера." : "Камера недоступна: проверьте, что сайт открыт по https и камера не занята другим приложением.");
      });
    return () => {
      done = true;
      if (timer) window.clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onScan]);

  return (
    <Modal open title="Наведите камеру на штрихкод" onCancel={onClose} footer={null} destroyOnHidden width={520}>
      {error ? (
        <Alert type="warning" showIcon message={error} />
      ) : (
        <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", background: "#000" }}>
          <video ref={video} muted playsInline style={{ width: "100%", display: "block", maxHeight: 420, objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: "30% 8%", border: "3px solid #4C7DFF", borderRadius: 12, pointerEvents: "none" }} />
        </div>
      )}
    </Modal>
  );
}
